import { useEffect, useState, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Loader2, Clock, Zap, Users, UserPlus, Trash2, Globe, Pencil, Check, X, ExternalLink, EyeOff, Eye, ChevronDown, Upload, Building2, MapPin } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AiUsagePanel } from "@/components/AiUsagePanel";

export default function Settings() {
  const { user, checkAuth } = useAuth();
  const readOnly = !!user?.is_member;

  const [sla, setSla] = useState({});
  const [syncedTypes, setSyncedTypes] = useState([]);
  const [excluded, setExcluded] = useState([]);
  const [hidden, setHidden] = useState([]);
  const [hiddenExpanded, setHiddenExpanded] = useState(false);
  const [skipWeekends, setSkipWeekends] = useState(false);
  const [savingSla, setSavingSla] = useState(false);

  const TEAM_PAGES = [["dashboard", "Dashboard"], ["calendar", "Calendar"], ["orders", "Orders"], ["workload", "Workload"], ["time-tracking", "Time Tracking"], ["clients", "Clients"], ["automations", "Automations"], ["music", "Music"], ["templates", "Templates"], ["settings", "Settings"]];
  const ROLE_PRESETS = {
    admin: TEAM_PAGES.map((p) => p[0]),
    photographer: ["dashboard", "calendar", "orders", "workload", "time-tracking"],
    editor: ["dashboard", "orders", "workload", "time-tracking"],
  };
  const permsFromRole = (role) => Object.fromEntries((ROLE_PRESETS[role] || []).map((k) => [k, true]));
  const [editors, setEditors] = useState([]);
  const [newEditor, setNewEditor] = useState({ name: "", email: "", link: "", home_location: "", role: "photographer", permissions: permsFromRole("photographer"), property_scope: "assigned" });
  const [addingEditor, setAddingEditor] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteSent, setInviteSent] = useState(null);
  const handleInvite = async () => {
    if (!editingMember) return;
    if (!newEditor.email.trim()) { toast.error("Add an email address first, then save"); return; }
    setInviting(true);
    try {
      const { data } = await api.post(`/editors/${editingMember}/invite`, { app_url: window.location.origin });
      const sentTo = data?.email || newEditor.email;
      setInviteSent({ email: sentTo, at: new Date().toISOString() });
      toast.success(`Invite sent to ${sentTo}`);
      await loadEditors();
    } catch (e) { toast.error(apiError(e)); }
    finally { setInviting(false); }
  };
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [savingEditor, setSavingEditor] = useState(false);

  const [tz, setTz] = useState("UTC");
  const [tzCommon, setTzCommon] = useState([]);
  const [savingTz, setSavingTz] = useState(false);

  const [biz, setBiz] = useState("");
  const [logo, setLogo] = useState(null);
  const [logoInvert, setLogoInvert] = useState(true);
  const [accent, setAccent] = useState("");
  const [savingBiz, setSavingBiz] = useState(false);
  const logoInputRef = useRef(null);

  useEffect(() => {
    if (user) {
      setBiz(user.business_name || "");
      setLogo(user.logo_url || null);
      setLogoInvert(user.logo_invert !== false);
      setAccent(user.accent_color || "");
    }
  }, [user]);

  const onLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new window.Image();
      img.onload = () => {
        const max = 256;
        let { width, height } = img;
        if (width > max || height > max) {
          const r = Math.min(max / width, max / height);
          width = Math.round(width * r); height = Math.round(height * r);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        setLogo(canvas.toDataURL("image/png"));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const saveBusiness = async () => {
    setSavingBiz(true);
    try {
      await api.put("/settings/business", { business_name: biz.trim() || null, logo_url: logo, logo_invert: logoInvert, accent_color: accent || null });
      await checkAuth();
      toast.success("Business info updated");
    } catch (err) { toast.error(apiError(err)); }
    finally { setSavingBiz(false); }
  };

  const loadSla = async () => {
    try {
      const { data } = await api.get("/settings/sla");
      setSla(data.sla);
      setSyncedTypes(data.synced_types || []);
      setExcluded(data.excluded || []);
      setHidden(data.hidden || []);
      setSkipWeekends(!!data.skip_weekends);
    } catch (err) { toast.error(apiError(err)); }
  };

  const loadEditors = async () => {
    try {
      const { data } = await api.get("/editors");
      setEditors(data);
    } catch (err) { toast.error(apiError(err)); }
  };

  const loadTz = async () => {
    try {
      const { data } = await api.get("/settings/timezones");
      setTz(data.current);
      setTzCommon(data.common);
    } catch (err) { toast.error(apiError(err)); }
  };

  useEffect(() => { loadSla(); loadEditors(); loadTz(); }, []);

  const saveTz = async () => {
    setSavingTz(true);
    try {
      await api.put("/settings/timezone", { timezone: tz });
      await checkAuth();
      await loadSla();
      toast.success("Timezone saved. All dates & due dates recalculated.");
    } catch (err) { toast.error(apiError(err)); }
    finally { setSavingTz(false); }
  };

  const saveSla = async () => {
    setSavingSla(true);
    try {
      await api.put("/settings/sla", { sla, excluded, hidden, skip_weekends: skipWeekends });
      const { data } = await api.post("/orders/recompute-due-dates");
      toast.success(`Saved. Recalculated due dates on ${data.orders_updated} orders.`);
    } catch (err) { toast.error(apiError(err)); }
    finally { setSavingSla(false); }
  };

  const toggleExclude = (type) => {
    setExcluded((prev) => prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]);
  };

  // Hiding is a cosmetic declutter of this list — persist it immediately so it
  // sticks without needing a full "recalculate" save.
  const persistHidden = async (next) => {
    try { await api.put("/settings/sla", { hidden: next }); }
    catch (err) { toast.error(apiError(err)); }
  };
  const hideType = (type) => {
    setHidden((prev) => { const next = [...new Set([...prev, type])]; persistHidden(next); return next; });
  };
  const unhideType = (type) => {
    setHidden((prev) => { const next = prev.filter((t) => t !== type); persistHidden(next); return next; });
  };

  const renderSlaRow = (t) => {
    const isExcluded = excluded.includes(t);
    const isHidden = hidden.includes(t);
    const safe = t.replace(/[^a-zA-Z0-9]/g, "-");
    return (
      <div key={t} data-testid={`sla-row-${safe}`}
        className={`flex flex-col justify-between rounded-lg border px-3.5 py-3 transition-colors ${isExcluded ? "border-border bg-secondary/10" : "border-border bg-secondary/40"}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <Switch checked={!isExcluded} onCheckedChange={() => toggleExclude(t)} data-testid={`sla-track-${safe}`} className="mt-0.5 shrink-0" />
            <span title={t} className={`min-w-0 flex-1 break-words text-sm font-medium leading-snug ${isExcluded ? "text-muted-foreground line-through" : ""}`}>{t}</span>
          </div>
          {isHidden ? (
            <button onClick={() => unhideType(t)} title="Unhide product" data-testid={`sla-unhide-${safe}`}
              className="-mr-1 -mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
              <Eye className="h-4 w-4" />
            </button>
          ) : (
            <button onClick={() => hideType(t)} title="Hide product" data-testid={`sla-hide-${safe}`}
              className="-mr-1 -mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-rose-400">
              <EyeOff className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="mt-3 flex items-center gap-2 pl-[30px]">
          <Input type="number" min="0" disabled={isExcluded}
            data-testid={`sla-input-${safe}`}
            value={sla[t] ?? 0}
            onChange={(e) => setSla({ ...sla, [t]: e.target.value === "" ? 0 : parseInt(e.target.value, 10) })}
            className="h-8 w-14 text-center text-sm" />
          <span className={`text-xs ${isExcluded ? "text-muted-foreground/60" : "text-muted-foreground"}`}>
            {isExcluded ? "not tracked" : "days after shoot"}
          </span>
        </div>
      </div>
    );
  };

  const openAddMember = () => {
    setEditingMember(null);
    setInviteSent(null);
    setNewEditor({ name: "", email: "", link: "", home_location: "", role: "photographer", permissions: permsFromRole("photographer"), property_scope: "assigned" });
    setShowAddDialog(true);
  };

  const startEditEditor = (ed) => {
    setInviteSent(null);
    setEditingMember(ed.id);
    setNewEditor({ name: ed.name, email: ed.email || "", link: ed.link || "", home_location: ed.home_location || "", role: ed.role || "photographer", permissions: ed.permissions || permsFromRole(ed.role || "photographer"), property_scope: ed.property_scope || "assigned" });
    setShowAddDialog(true);
  };

  const applyRolePreset = (role) => {
    setNewEditor((prev) => ({ ...prev, role, permissions: role === "custom" ? prev.permissions : permsFromRole(role) }));
  };
  const togglePage = (key) => {
    setNewEditor((prev) => ({ ...prev, role: "custom", permissions: { ...prev.permissions, [key]: !prev.permissions?.[key] } }));
  };

  const submitMember = async (e) => {
    e.preventDefault();
    if (!newEditor.name.trim()) { toast.error("Name is required"); return; }
    setAddingEditor(true);
    try {
      const payload = { name: newEditor.name.trim(), email: newEditor.email.trim() || null, link: newEditor.link.trim() || null, home_location: newEditor.home_location.trim() || null, role: newEditor.role, permissions: newEditor.permissions || {}, property_scope: newEditor.property_scope };
      if (editingMember) await api.patch(`/editors/${editingMember}`, payload);
      else await api.post("/editors", payload);
      setShowAddDialog(false);
      setEditingMember(null);
      setNewEditor({ name: "", email: "", link: "", home_location: "" });
      await loadEditors();
      toast.success(editingMember ? "Team member updated" : "Team member added");
    } catch (err) { toast.error(apiError(err)); }
    finally { setAddingEditor(false); }
  };

  const removeEditor = async (id) => {
    try {
      await api.delete(`/editors/${id}`);
      setDeleteTarget(null);
      await loadEditors();
      toast.success("Team member removed");
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div>
      <PageHeader title="Settings" subtitle="Manage your timezone, due-date rules & editing team" testid="settings-header" />
      <div className="mx-auto max-w-2xl space-y-6 p-6 md:p-8">
        <AiUsagePanel />
        {readOnly && (
          <div data-testid="member-readonly-banner" className="flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-300">
            <Users className="mt-0.5 h-4 w-4 shrink-0" />
            <span>You're a team member on this workspace. Settings are managed by the account owner and are read-only for you.</span>
          </div>
        )}
        {/* Timezone */}
        <div className="rounded-xl border border-border bg-card p-6" data-testid="timezone-settings">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-cyan-500/15 ring-1 ring-cyan-500/30">
              <Globe className="h-5 w-5 text-cyan-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-lg font-bold tracking-tight">Timezone</h3>
              <p className="text-sm text-muted-foreground">All shoot times, appointments and due dates are shown and calculated in this timezone.</p>
            </div>
          </div>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Select value={tz} onValueChange={setTz}>
              <SelectTrigger className="sm:w-[280px]" data-testid="timezone-select">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                {[...new Set([tz, ...tzCommon])].map((z) => (
                  <SelectItem key={z} value={z}>{z.replace(/_/g, " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={saveTz} disabled={savingTz || readOnly} data-testid="save-timezone-btn" className="gap-2">
              {savingTz ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Save timezone
            </Button>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">Current: <span className="font-mono text-foreground">{tz.replace(/_/g, " ")}</span></p>
        </div>

        {/* Auto due dates / SLA */}
        <div className="rounded-xl border border-border bg-card p-6" data-testid="sla-settings">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-500/15 ring-1 ring-amber-500/30">
              <Clock className="h-5 w-5 text-amber-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-lg font-bold tracking-tight">Automatic Due Dates</h3>
              <p className="text-sm text-muted-foreground">Set how many days after the shoot each synced product is due. Toggle off any product you don't want tracked in the workflow.</p>
            </div>
          </div>

          <div className="mt-5 flex items-center justify-between gap-3 rounded-lg border border-border bg-secondary/30 px-4 py-3" data-testid="skip-weekends-row">
            <div>
              <div className="text-sm font-medium">Skip weekends</div>
              <p className="text-xs text-muted-foreground">Count only business days — due dates land Monday–Friday.</p>
            </div>
            <Switch checked={skipWeekends} onCheckedChange={setSkipWeekends} data-testid="skip-weekends-toggle" />
          </div>

          {syncedTypes.length === 0 ? (
            <div className="mt-6 rounded-lg border border-dashed border-border py-8 text-center text-sm text-muted-foreground" data-testid="sla-empty">
              No products synced yet. Run "Sync from Aryeo" first, then your product types will appear here.
            </div>
          ) : (
            <>
              <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="sla-visible-grid">
                {syncedTypes.filter((t) => !hidden.includes(t)).map(renderSlaRow)}
              </div>

              {syncedTypes.some((t) => hidden.includes(t)) && (
                <div className="mt-4 overflow-hidden rounded-lg border border-border" data-testid="sla-hidden-section">
                  <button onClick={() => setHiddenExpanded((v) => !v)} data-testid="sla-hidden-toggle"
                    className="flex w-full items-center justify-between gap-2 bg-secondary/30 px-4 py-3 text-left transition-colors hover:bg-secondary/50">
                    <div className="flex items-center gap-2">
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Hidden</span>
                      <span className="rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground" data-testid="sla-hidden-count">
                        {syncedTypes.filter((t) => hidden.includes(t)).length}
                      </span>
                    </div>
                    <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${hiddenExpanded ? "rotate-180" : ""}`} />
                  </button>
                  {hiddenExpanded && (
                    <div className="grid grid-cols-1 gap-3 border-t border-border p-3 sm:grid-cols-2" data-testid="sla-hidden-grid">
                      {syncedTypes.filter((t) => hidden.includes(t)).map(renderSlaRow)}
                    </div>
                  )}
                </div>
              )}

              <Button onClick={saveSla} disabled={savingSla || readOnly} data-testid="save-sla-btn" className="mt-5 gap-2">
                {savingSla ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                Save & recalculate due dates
              </Button>
            </>
          )}
        </div>

        {/* Editors */}
        <div className="rounded-xl border border-border bg-card p-6" data-testid="editors-settings">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-500/15 ring-1 ring-purple-500/30">
              <Users className="h-5 w-5 text-purple-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-lg font-bold tracking-tight">Team</h3>
              <p className="text-sm text-muted-foreground">Manage your team. Assign members to deliverables, and set a photographer's home location so dashboard routes start &amp; end there.</p>
            </div>
            <Button onClick={openAddMember} disabled={readOnly} data-testid="add-editor-btn" className="gap-2">
              <UserPlus className="h-4 w-4" /> Add team member
            </Button>
          </div>

          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogContent data-testid="add-team-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {editingMember ? "Edit team member" : "Add team member"}
                  {editingMember && editors.find((e) => e.id === editingMember)?.invite_status === "accepted" && (
                    <span data-testid="member-joined-tag" className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-medium text-emerald-600 dark:text-emerald-400">Joined</span>
                  )}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={submitMember} className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Name</Label>
                  <Input placeholder="Full name" value={newEditor.name} autoFocus data-testid="editor-name-input"
                    onChange={(e) => setNewEditor({ ...newEditor, name: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Email <span className="text-muted-foreground">(optional)</span></Label>
                  <Input placeholder="name@example.com" value={newEditor.email} data-testid="editor-email-input"
                    onChange={(e) => setNewEditor({ ...newEditor, email: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Link <span className="text-muted-foreground">(optional, e.g. Frame.io)</span></Label>
                  <Input placeholder="https://…" value={newEditor.link} data-testid="editor-link-input"
                    onChange={(e) => setNewEditor({ ...newEditor, link: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Home / starting location <span className="text-muted-foreground">(for photographer routes)</span></Label>
                  <Input placeholder="123 Main St, City, ST" value={newEditor.home_location} data-testid="editor-home-input"
                    onChange={(e) => setNewEditor({ ...newEditor, home_location: e.target.value })} />
                  <p className="text-xs text-muted-foreground">Used as the start &amp; end point when calculating this photographer's route on the dashboard map.</p>
                </div>
                <div className="space-y-2 rounded-lg border border-border bg-secondary/20 p-3">
                  <Label>Permissions</Label>
                  <div className="flex flex-wrap gap-1.5" data-testid="team-role-presets">
                    {[["admin", "Admin"], ["photographer", "Photographer"], ["editor", "Editor"], ["custom", "Custom"]].map(([k, lbl]) => (
                      <button type="button" key={k} onClick={() => applyRolePreset(k)} data-testid={`role-preset-${k}`}
                        className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${newEditor.role === k ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>{lbl}</button>
                    ))}
                  </div>
                  <p className="pt-1 text-xs text-muted-foreground">Page access</p>
                  <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                    {TEAM_PAGES.map(([key, label]) => (
                      <label key={key} className="flex cursor-pointer items-center gap-2 text-xs" data-testid={`perm-page-${key}`}>
                        <input type="checkbox" checked={!!newEditor.permissions?.[key]} onChange={() => togglePage(key)}
                          className="h-3.5 w-3.5 rounded border-border accent-primary" />
                        {label}
                      </label>
                    ))}
                  </div>
                  <p className="pt-1 text-xs text-muted-foreground">Property visibility</p>
                  <div className="flex gap-1.5" data-testid="team-scope">
                    {[["all", "All properties"], ["assigned", "Only assigned to them"]].map(([k, lbl]) => (
                      <button type="button" key={k} onClick={() => setNewEditor({ ...newEditor, property_scope: k })} data-testid={`scope-${k}`}
                        className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${newEditor.property_scope === k ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>{lbl}</button>
                    ))}
                  </div>
                </div>
                <DialogFooter>
                  {inviteSent && (
                    <div data-testid="invite-sent-banner" className="mb-2 flex w-full items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-600 dark:text-emerald-400">
                      <CheckCircle2 className="h-4 w-4 shrink-0" />
                      <span>Invitation email sent to <span className="font-medium">{inviteSent.email}</span>. They'll set a password to join your workspace.</span>
                    </div>
                  )}
                  {editingMember && (
                    <Button type="button" variant="outline" onClick={handleInvite} disabled={inviting} data-testid="invite-member-btn" className="mr-auto gap-2">
                      {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                      {inviteSent || ["accepted", "pending"].includes(editors.find((e) => e.id === editingMember)?.invite_status) ? "Re-Send Invite" : "Send invite"}
                    </Button>
                  )}
                  <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                  <Button type="submit" disabled={addingEditor} data-testid="save-team-btn" className="gap-2">
                    {addingEditor ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                    {editingMember ? "Save changes" : "Add member"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
            <DialogContent data-testid="delete-team-dialog">
              <DialogHeader>
                <DialogTitle>Remove {deleteTarget?.name}?</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>This permanently removes <span className="font-medium text-foreground">{deleteTarget?.name}</span> from your team.</p>
                <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-foreground">
                  <p className="font-medium text-destructive">This will unlink them from all history:</p>
                  <ul className="mt-1.5 list-disc space-y-0.5 pl-5 text-xs text-muted-foreground">
                    <li>Their editor assignments on every deliverable will be cleared</li>
                    <li>Their name will no longer link to tracked time entries in reports</li>
                    <li>Their home location for photographer routes will be removed</li>
                  </ul>
                  <p className="mt-2 text-xs">Past orders and time already logged are kept, but will no longer be attributed to this member. This cannot be undone.</p>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
                <Button type="button" variant="destructive" onClick={() => removeEditor(deleteTarget.id)} data-testid="confirm-delete-team-btn">
                  Remove member
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <div className="mt-5 space-y-2">
            {editors.length === 0 ? (
              <p className="text-sm text-muted-foreground">No team members yet. Add your first team member above.</p>
            ) : (
              editors.map((ed) => (
                <div key={ed.id} className="flex items-center justify-between gap-3 rounded-lg border border-border bg-secondary/30 px-4 py-2.5" data-testid={`editor-row-${ed.id}`}>
                  {(
                    <>
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: ed.color }} />
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium">{ed.name}</div>
                          {ed.email && <div className="truncate text-xs text-muted-foreground">{ed.email}</div>}
                          {ed.link && (
                            <a href={ed.link} target="_blank" rel="noreferrer" data-testid={`editor-row-link-${ed.id}`}
                              className="inline-flex items-center gap-1 truncate text-xs text-primary hover:underline">
                              <ExternalLink className="h-3 w-3 shrink-0" />{ed.link.replace(/^https?:\/\//, "")}
                            </a>
                          )}
                          {ed.home_location && (
                            <div className="flex items-center gap-1 truncate text-xs text-muted-foreground" data-testid={`editor-row-home-${ed.id}`}>
                              <MapPin className="h-3 w-3 shrink-0" />{ed.home_location}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {ed.invite_status && (
                          <span data-testid={`invite-status-${ed.id}`}
                            className={`mr-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${ed.invite_status === "accepted" ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "bg-amber-500/15 text-amber-600 dark:text-amber-400"}`}>
                            {ed.invite_status === "accepted" ? "Joined" : "Invited"}
                          </span>
                        )}
                        <button onClick={() => startEditEditor(ed)} data-testid={`edit-editor-${ed.id}`}
                          className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-primary">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button onClick={() => setDeleteTarget(ed)} data-testid={`delete-editor-${ed.id}`}
                          className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Account */}
        <div className="rounded-xl border border-border bg-card p-6" data-testid="account-settings">
          <h3 className="font-heading text-lg font-bold tracking-tight">Account</h3>

          <div className="mt-4 space-y-4 rounded-lg border border-border p-4" data-testid="business-settings">
            <div>
              <Label htmlFor="business-name">Business name</Label>
              <Input id="business-name" value={biz} onChange={(e) => setBiz(e.target.value)} placeholder="HomePix Ops"
                className="mt-1.5" data-testid="business-name-input" />
              <p className="mt-1 text-xs text-muted-foreground">Shown at the top-left of every page.</p>
            </div>
            <div>
              <Label>Logo</Label>
              <div className="mt-1.5 flex items-center gap-3">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-secondary">
                  {logo ? <img src={logo} alt="logo" className="h-full w-full object-contain" data-testid="business-logo-preview" />
                    : <Building2 className="h-6 w-6 text-muted-foreground" />}
                </div>
                <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={onLogoFile} data-testid="business-logo-input" />
                <Button variant="outline" size="sm" className="gap-2" onClick={() => logoInputRef.current?.click()} data-testid="business-logo-upload">
                  <Upload className="h-3.5 w-3.5" /> {logo ? "Change" : "Upload"} logo
                </Button>
                {logo && (
                  <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground" onClick={() => setLogo(null)} data-testid="business-logo-clear">
                    <X className="h-3.5 w-3.5" /> Remove
                  </Button>
                )}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">Square images work best. Automatically resized.</p>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Invert logo in dark mode</Label>
                <p className="text-xs text-muted-foreground">Turn on for dark/black logos so they stay visible.</p>
              </div>
              <Switch checked={logoInvert} onCheckedChange={setLogoInvert} data-testid="business-logo-invert" />
            </div>
            <div>
              <Label>Accent color</Label>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                {["#2563eb", "#7c3aed", "#db2777", "#e11d48", "#ea580c", "#059669", "#0891b2"].map((c) => (
                  <button key={c} type="button" onClick={() => setAccent(c)} title={c} data-testid={`accent-${c}`}
                    className={`h-7 w-7 rounded-full ring-2 ring-offset-2 ring-offset-card transition-all ${accent.toLowerCase() === c ? "ring-foreground" : "ring-transparent"}`}
                    style={{ backgroundColor: c }} />
                ))}
                <input type="color" value={accent || "#2563eb"} onChange={(e) => setAccent(e.target.value)} data-testid="accent-custom"
                  className="h-7 w-9 cursor-pointer rounded border border-border bg-transparent" />
                {accent && (
                  <button type="button" onClick={() => setAccent("")} data-testid="accent-reset" className="text-xs text-muted-foreground hover:text-foreground">Reset</button>
                )}
              </div>
            </div>
            <Button onClick={saveBusiness} disabled={savingBiz || readOnly} className="gap-2" data-testid="business-save-btn">
              {savingBiz && <Loader2 className="h-4 w-4 animate-spin" />} Save business info
            </Button>
          </div>

          <dl className="mt-4 space-y-3 text-sm">
            <div className="flex justify-between"><dt className="text-muted-foreground">Name</dt><dd>{user?.name}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Email</dt><dd>{user?.email}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Role</dt><dd className="capitalize">{user?.role}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Sign-in method</dt><dd className="capitalize">{user?.auth_provider}</dd></div>
          </dl>
        </div>
      </div>
    </div>
  );
}
